import {
	Avatar,
	AvatarBadge,
	Text,
	Tooltip,
	Box,
	VStack,
	Button,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	useDisclosure,
	Textarea,
	StackDivider,
	useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import RateItem from "./RateItem";
import { RateLevels } from "../../constants/consultant";
import { useConsultantRating } from "@api/hooks/useConsultants";

interface Props {
	imageUrl: string;
	name: string;
	status: "PENDING_ANSWER" | "DECLINED" | "CANCELLED" | "ACCEPTED";
	boxSize?: string;
	hasRated?: boolean;
	consultantId?: string;
	jobId?: string;
	customerId?: string;
	organizationId?: string;
	emailFeedback?: boolean;
}

const ConsultantAvatar = ({
	imageUrl,
	name,
	status,
	boxSize = "75px",
	hasRated = false,
	consultantId,
	jobId,
	customerId,
	organizationId,
	emailFeedback = false,
}: Props) => {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();
	const [performance, setPerformance] = useState("");
	const [initiallyOpened, setInitiallyOpened] = useState(false);
	const [punctualityScore, setPunctualityScore] = useState("");
	const [comment, setComment] = useState("");

	const { mutate: addRating, isLoading: isLoadingCreateRating } = useConsultantRating(
		consultantId,
		() => {
			toast({
				title: "Framgångsrikt lagt till betyg för konsulten",
				status: "success",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
			onClose();
		},
		() => {
			toast({
				title: "Det gick inte att lägga till betyg för konsulten",
				status: "error",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		},
	);

	const getAvatarBGColor = (status: "PENDING_ANSWER" | "DECLINED" | "CANCELLED" | "ACCEPTED"): string => {
		if (status === "ACCEPTED") return "#4BB769";
		if (status === "DECLINED") return "#FF0000";
		return "#DEDEDE";
	};

	const getTooltip = (status: "PENDING_ANSWER" | "DECLINED" | "CANCELLED" | "ACCEPTED"): string => {
		if (status === "ACCEPTED") return "Bokad";
		if (status === "DECLINED") return "Kan inte";
		return "Inväntar svar";
	};

	const getIcon = (status: "PENDING_ANSWER" | "DECLINED" | "CANCELLED" | "ACCEPTED"): React.ReactNode => {
		if (status === "ACCEPTED")
			return (
				<svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M8.57703 1.07768L3.33286 6.32184L1.42203 4.41101C1.26486 4.25921 1.05436 4.17522 0.835858 4.17711C0.617361 4.17901 0.40835 4.26665 0.253843 4.42116C0.0993366 4.57567 0.0116958 4.78468 0.00979713 5.00318C0.00789844 5.22167 0.0918938 5.43217 0.243692 5.58934L2.74369 8.08934C2.89997 8.24557 3.11189 8.33333 3.33286 8.33333C3.55383 8.33333 3.76575 8.24557 3.92203 8.08934L9.75536 2.25601C9.90716 2.09884 9.99115 1.88834 9.98925 1.66984C9.98736 1.45134 9.89971 1.24233 9.74521 1.08783C9.5907 0.933321 9.38169 0.84568 9.16319 0.843782C8.9447 0.841883 8.73419 0.925878 8.57703 1.07768Z"
						fill="white"
					/>
				</svg>
			);
		if (status === "DECLINED")
			return (
				<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M8.32857 0.671429C8.1 0.442857 7.75714 0.442857 7.52857 0.671429L4.5 3.7L1.47143 0.671429C1.24286 0.442857 0.9 0.442857 0.671429 0.671429C0.442857 0.9 0.442857 1.24286 0.671429 1.47143L3.7 4.5L0.671429 7.52857C0.442857 7.75714 0.442857 8.1 0.671429 8.32857C0.785714 8.44286 0.9 8.5 1.07143 8.5C1.24286 8.5 1.35714 8.44286 1.47143 8.32857L4.5 5.3L7.52857 8.32857C7.64286 8.44286 7.81429 8.5 7.92857 8.5C8.04286 8.5 8.21429 8.44286 8.32857 8.32857C8.55714 8.1 8.55714 7.75714 8.32857 7.52857L5.3 4.5L8.32857 1.47143C8.55714 1.24286 8.55714 0.9 8.32857 0.671429Z"
						fill="white"
					/>
				</svg>
			);
		return (
			<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M6 3.08301V5.99967H8.91667" stroke="#888888" strokeLinecap="round" strokeLinejoin="round" />
				<path
					d="M5.99935 10.5832C8.53065 10.5832 10.5827 8.53114 10.5827 5.99984C10.5827 3.46853 8.53065 1.4165 5.99935 1.4165C3.46804 1.4165 1.41602 3.46853 1.41602 5.99984C1.41602 8.53114 3.46804 10.5832 5.99935 10.5832Z"
					stroke="#888888"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		);
	};

	const onRate = async () => {
		if (!performance || !punctualityScore || !consultantId || !jobId || !customerId || !addRating || !organizationId) {
			toast({
				title: "Det gick inte att lägga till betyg för konsulten",
				status: "error",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		} else {
			try {
				await addRating({
					comment,
					customer: { id: customerId },
					job: { id: jobId },
					performance: Number(performance),
					punctualityScore: Number(punctualityScore),
					organisation: { id: organizationId },
				});
			} catch (err) {
				console.error(err);
				toast({
					title: "Det gick inte att lägga till betyg för konsulten",
					status: "error",
					duration: 5000,
					isClosable: true,
					variant: "subtle",
				});
			}
		}
	};

	useEffect(() => {
		if (emailFeedback && !isOpen && !initiallyOpened) {
			onOpen();
			setInitiallyOpened(true);
		}
	}, [emailFeedback, onOpen, isOpen, initiallyOpened]);

	return (
		<VStack>
			<Tooltip
				borderRadius={"7px"}
				border={"1px solid #E6E6E6"}
				boxShadow="0px 1px 4px 0px #00000026"
				bg={"#FFFFFF"}
				color={"#999999"}
				label={getTooltip(status)}
			>
				<Box width={boxSize} height={boxSize}>
					<Avatar width={boxSize} height={boxSize} name={name ? name : "Consultant Name"} src={imageUrl}>
						<AvatarBadge boxSize="1.25em" bg={getAvatarBGColor(status)}>
							{getIcon(status)}
						</AvatarBadge>
					</Avatar>
					<Text fontSize={"14px"} fontWeight={400} m={0} textAlign={"center"}>
						{name}
					</Text>
				</Box>
			</Tooltip>
			<Button
				my={3}
				onClick={onOpen}
				variant={"link"}
				fontSize={"12px"}
				fontWeight={600}
				color={hasRated ? "#999999" : "#219CD0"}
				isDisabled={hasRated}
				_disabled={{ color: "#999999", cursor: "not-allowed" }}
			>
				{hasRated ? "Omdöme lämnat" : "Lämna omdöme"}
			</Button>
			<Modal isOpen={isOpen && status !== "PENDING_ANSWER"} onClose={onClose}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader fontWeight={500} fontSize={"20px"} color={"#353535"} textAlign={"center"}>
						{`Lämna omdöme för ${name}`}
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<VStack divider={<StackDivider borderColor="gray.200" />}>
							<Text align={"center"} fontWeight={400} fontSize={"15px"} color={"#2A2A2A"}>
								{`Hur var er upplevelse av att arbeta med ${name}? Betygsätt från 1-5, där 1 betyder "instämmer inte alls"
								och 5 betyder "instämmer helt".`}
							</Text>
							<RateItem
								onChange={(key) => setPunctualityScore(key)}
								label="Konsulten var punktlig *"
								rateLevels={RateLevels}
								selectedKey={punctualityScore}
							/>
							<RateItem
								onChange={(key) => setPerformance(key)}
								label="Konsulten gjorde ett bra jobb *"
								rateLevels={RateLevels}
								selectedKey={performance}
							/>
							<VStack>
								<Text mb={0} color={"#999999"} fontSize={"15px"} fontWeight={500}>
									{"Lämna en kommentar"}
								</Text>
								<Textarea
									minW={"300px"}
									value={comment}
									onChange={(e) => {
										setComment(e.target.value);
									}}
								/>
							</VStack>
						</VStack>
					</ModalBody>
					<ModalFooter mb={5}>
						<VStack width={"100%"}>
							<Button
								borderRadius={"2px"}
								_hover={{ background: "#4e7da7bd" }}
								bg="#4e7ea7"
								color={"#FFFFFF"}
								width={300}
								mx={3}
								onClick={onRate}
								isLoading={isLoadingCreateRating}
								isDisabled={!performance || !punctualityScore}
							>
								Skicka omdöme
							</Button>
							<Button borderRadius={"2px"} width={300} mx={3} onClick={onClose} isLoading={isLoadingCreateRating}>
								Avbryt
							</Button>
						</VStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</VStack>
	);
};

export default ConsultantAvatar;
